import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useCallback, useEffect } from 'react';
import type { TeamSummaryProjection } from '@projoint/shared';

import { useTeams } from '@/shared/api';

/** The AI hub's explicit team (`?team=<slug>`, §5 W4-D) — shared by /ai, /agents, /approvals. */
export interface AiHubSearch {
  team?: string;
}

export function validateAiHubSearch(raw: Record<string, unknown>): AiHubSearch {
  return typeof raw.team === 'string' && raw.team ? { team: raw.team } : {};
}

/** The viewer's last explicit pick (a per-browser convenience, never a default guess). */
const STORAGE_KEY = 'projoint:ai-hub-team';

function readRemembered(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function remember(slug: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, slug);
  } catch {
    /* private mode / blocked storage — the URL still carries the choice */
  }
}

export interface HubTeam {
  /** The team the hub works in (its agents, approvals, AI key) — null until chosen. */
  team: TeamSummaryProjection | null;
  /** Regular teams by default; Assistant opts into Personal explicitly. */
  teams: TeamSummaryProjection[];
  /** Several teams and no choice yet: the page asks instead of guessing. */
  needsChoice: boolean;
  isLoading: boolean;
  choose(slug: string): void;
}

/**
 * The AI hub's EXPLICIT team (`?team=<slug>`, §5 W4-D): Assistant · Agents ·
 * Approvals all work in the team the picker shows. Never silently the first
 * team: with one team it is that team; with several it is the last team you
 * picked here (written back into the URL, so it is visible and shareable),
 * else the page asks.
 */
export function useHubTeam(opts: { includePersonal?: boolean } = {}): HubTeam {
  const navigate = useNavigate();
  const teamsQuery = useTeams();
  const teams = (teamsQuery.data ?? []).filter((t) => t.kind === 'regular' || opts.includePersonal);
  const fromUrl = useRouterState({
    select: (s) => {
      const v = (s.location.search as Record<string, unknown>).team;
      return typeof v === 'string' ? v : null;
    },
  });
  const team = teams.find((t) => t.slug === fromUrl) ?? null;

  const choose = useCallback(
    (slug: string) => {
      remember(slug);
      void navigate({
        search: ((prev: Record<string, unknown>) => ({ ...prev, team: slug })) as never,
        replace: true,
      });
    },
    [navigate],
  );

  // No (valid) `?team=`: fill in the only team, or the one picked here before.
  const fallback =
    team || !teamsQuery.data
      ? null
      : teams.length === 1
        ? teams[0]!.slug
        : (teams.find((t) => t.slug === readRemembered())?.slug ?? null);
  useEffect(() => {
    if (fallback) {
      void navigate({
        search: ((prev: Record<string, unknown>) => ({ ...prev, team: fallback })) as never,
        replace: true,
      });
    }
  }, [fallback, navigate]);

  return {
    team,
    teams,
    needsChoice: !!teamsQuery.data && !team && !fallback && teams.length > 1,
    isLoading: teamsQuery.isLoading || (!team && !!fallback),
    choose,
  };
}
